import React from "react";
import { Megaphone, Users, FileText, Mail, Clock } from "lucide-react";
import { useSettings } from "context/SettingsContext";
import { resolveDatePrefs } from "services/date-converter";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
    <Icon size={15} className="text-text_muted flex-shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <div className="text-[11px] text-text_muted">{label}</div>
      <div className="text-xs font-medium text-text_primary truncate">{value}</div>
    </div>
  </div>
);

const pad2 = (n) => String(n).padStart(2, "0");

// `scheduledAt` is a wall-clock container (its own getFullYear/getHours etc.
// already *are* the account-timezone fields the user picked in the wizard —
// see useCampaignWizard's `scheduledUtc`), so this formats those fields
// directly rather than re-interpreting the instant through a timezone.
const formatScheduled = (date, { dateFormat, timeFormat }) => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  const datePart =
    dateFormat === "MM/DD/YYYY" ? `${m}/${d}/${y}` :
    dateFormat === "YYYY-MM-DD" ? `${y}-${m}-${d}` :
    dateFormat === "DD-MM-YYYY" ? `${d}-${m}-${y}` :
    `${d}/${m}/${y}`;

  const h24 = date.getHours();
  const min = pad2(date.getMinutes());
  const timePart =
    timeFormat === "12"
      ? `${pad2(h24 % 12 || 12)}:${min} ${h24 < 12 ? "AM" : "PM"}`
      : `${pad2(h24)}:${min}`;

  return `${datePart}, ${timePart}`;
};

const StepReview = ({ formData, resolvedRecipients, selectedTemplate, sendMode, scheduledAt, timeZone }) => {
  const { settings } = useSettings() || {};
  const { dateFormat, timeFormat } = resolveDatePrefs(settings);

  const sendTimeValue =
    sendMode === "SCHEDULED" && scheduledAt
      ? `${formatScheduled(scheduledAt, { dateFormat, timeFormat })} (${timeZone})`
      : "Immediately";

  return (
    <div className="space-y-4">
      <p className="text-xs text-text_secondary">
        {sendMode === "SCHEDULED"
          ? "Review everything before scheduling — the campaign will send automatically at the chosen time."
          : "Review everything before sending — sending happens in the background once you click Done."}
      </p>

      <div className="rounded-lg border border-border px-3">
        <Row icon={Megaphone} label="Campaign Name" value={formData.campaign_name} />
        <Row icon={Users} label="Audience" value={`${resolvedRecipients.length} recipient(s)`} />
        <Row icon={FileText} label="Template" value={selectedTemplate?.template_name || "—"} />
        <Row icon={Mail} label="Subject" value={selectedTemplate?.subject || "—"} />
        <Row icon={Clock} label="Send Time" value={sendTimeValue} />
      </div>

      {selectedTemplate?.body_html && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text_muted mb-1.5">Preview</div>
          <div
            className="rounded-lg border border-border bg-surface_alt p-3 text-xs text-text_secondary max-h-[220px] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: selectedTemplate.body_html }}
          />
        </div>
      )}
    </div>
  );
};

export default StepReview;
